import { DOCUMENT, Location } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import {
  NavigationStart,
  Params,
  QueryParamsHandling,
  Router,
} from '@angular/router';
import { languageActions } from '@epicuro-next/platform/i18n';
import { selectAccountId } from '@epicuro-next/state/session-state';
import { isNonNullish } from '@epicuro-next/utilities';
import {
  getPulseUIRedirectTo,
  setPulseUIRedirectTo,
} from '@epicuro-next/utilities-browser';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { combineLatest, withLatestFrom } from 'rxjs';
import { filter, take, tap } from 'rxjs/operators';

import { ROUTER_OUTLET_KEY } from './constants';
import {
  back,
  forward,
  go,
  goExternal,
  redirectToUserUrl,
  updateQueryParameters,
} from './router.actions';
import { selectQueryParams } from './router.selectors';

/**
 * Basic navigation utility action side-effect
 */
@Injectable()
export class RouterEffects {
  /**
   * Side Effect for navigation request.
   */
  public go$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(go),
        withLatestFrom(this.store.pipe(select(selectQueryParams))),
        tap(([payload, currentParams]) => {
          if (payload.extras?.state?.[ROUTER_OUTLET_KEY] === true) {
            this.router.navigate(payload.commands, payload.extras);
            return;
          }

          let path = payload.commands.join('/');
          const searchParams = this.extractQueryParams(
            payload.extras?.queryParams ?? {},
            currentParams,
            payload.extras?.queryParamsHandling,
          );

          if (searchParams) {
            path += `?${new URLSearchParams(searchParams).toString()}`;
          }
          this.router.navigateByUrl(path, payload.extras);
          return;
        }),
      ),
    { dispatch: false },
  );

  /**
   * Side Effect for updating the query parameters
   */
  public updateQueryParameters$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(updateQueryParameters),
        tap((payload) => {
          this.router.navigate([], {
            queryParams: payload.queryParams,
            queryParamsHandling: payload.queryParamsHandling ?? 'merge',
            replaceUrl: payload.replaceUrl ?? true,
          });
        }),
      );
    },
    { dispatch: false },
  );

  /**
   * Side Effect for navigation external request.
   */
  public goExternal$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(goExternal),
        tap((payload) => {
          const path = payload.commands.join('/');

          let params;
          if (payload.extras?.queryParams) {
            const queryParams: Params = payload.extras.queryParams;

            params = Location.normalizeQueryParams(
              Object.keys(queryParams)
                .map((key: string) => `${key}=${queryParams[key]}`)
                .join('&'),
            );
          }

          this.document.location.href = `/#/${path}${params ?? ''}`;
        }),
      );
    },
    { dispatch: false },
  );

  /**
   * Side Effect for navigation back in history.
   */
  public back$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(back),
        tap(() => {
          this.location.back();
        }),
      );
    },
    { dispatch: false },
  );

  /**
   * Side Effect for navigating forward in in history.
   */
  public forward$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(forward),
        tap(() => {
          this.location.forward();
        }),
      );
    },
    { dispatch: false },
  );

  public routeRedirectToUserUrl$ = createEffect(
    () =>
      combineLatest([
        this.router.events.pipe(filter((ev) => ev instanceof NavigationStart)),
        this.actions$.pipe(ofType(languageActions.languageReady)),
        this.store.pipe(select(selectAccountId), filter(isNonNullish)),
      ]).pipe(
        tap(() => {
          const url = this.getAndClearUserSavedUrl();
          if (url) {
            this.store.dispatch(redirectToUserUrl({ url }));
          }
        }),
        take(1),
      ),
    { dispatch: false },
  );

  /**
   * Dependencies of router effects.
   */
  constructor(
    private actions$: Actions,
    private router: Router,
    private location: Location,
    private store: Store,
    @Inject(DOCUMENT) private document: Document,
  ) {}

  private getAndClearUserSavedUrl(): string | undefined {
    const redirectToUrl = getPulseUIRedirectTo();
    if (redirectToUrl) {
      setPulseUIRedirectTo();
    }

    return redirectToUrl ?? undefined;
  }

  private extractQueryParams(
    queryParams: Params,
    currentParams: Params,
    queryParamsHandling?: QueryParamsHandling | null,
  ) {
    const navigation = this.router.getCurrentNavigation();
    const filteredParams = Object.fromEntries(
      Object.entries(queryParams).filter(([_, value]) => isNonNullish(value)),
    );

    switch (queryParamsHandling) {
      case 'merge': {
        return {
          // navigation is not null only for BS (due to specific in addSiteIdIfNotExist$ effect), but not for single site tenants, so
          // currentParams from store is used for others
          ...(navigation?.previousNavigation?.extractedUrl?.queryParams ??
            currentParams ??
            {}),
          ...(filteredParams ?? {}),
        };
      }
      case 'preserve': {
        return navigation?.previousNavigation?.extractedUrl?.queryParams;
      }
      default: {
        return filteredParams || null;
      }
    }
  }
}
